// client/src/services/AvailabilityService.js
import BaseService from './BaseService'
import flex from '../api/FlexFetch'

/**
 * Service for managing player availability - creating, retrieving, and managing availability slots
 */
class AvailabilityService extends BaseService {
  constructor() {
    super()
    this.endpoint = '/api/1.0/availability/'
  }

  /**
   * Create a new availability slot
   * @param {Object} availabilityData - Availability data object
   * @returns {Promise<Object>} Service response object
   */
  async createAvailability(availabilityData) {
    return this.execute(async () => {
      this.validateParams({ availabilityData }, ['availabilityData'])

      const response = await flex.flexFetch(this.endpoint + 'post', {
        method: 'POST',
        body: JSON.stringify(availabilityData),
        credentials: 'include',
      })

      return this._unwrapStrictResponse(await flex.handleResponse(response))
    }, 'Create availability')
  }

  /**
   * Get current user's availability
   * @param {number} leagueId - Optional league ID to filter by
   * @param {number} divisionId - Optional division ID to filter by
   * @returns {Promise<Object>} Service response object with availability data
   */
  async getMyAvailability(leagueId = null, divisionId = null) {
    return this.execute(async () => {
      // Build query parameters
      const params = new URLSearchParams()
      if (leagueId) params.append('league_id', leagueId)
      if (divisionId) params.append('division_id', divisionId)

      const queryString = params.toString()
      const url = this.endpoint + 'get' + (queryString ? `?${queryString}` : '')

      const response = await flex.flexFetch(url, {
        credentials: 'include',
      })

      return this._unwrapStrictResponse(await flex.handleResponse(response))
    }, 'Get my availability')
  }

  /**
   * Delete an availability slot
   * @param {number} availabilityId - ID of the availability slot to delete
   * @returns {Promise<Object>} Service response object
   */
  async deleteAvailability(availabilityId) {
    return this.execute(async () => {
      this.validateParams({ availabilityId }, ['availabilityId'])

      const response = await flex.flexFetch(this.endpoint + 'delete', {
        method: 'DELETE',
        body: JSON.stringify({ id: availabilityId }),
        credentials: 'include',
      })

      return this._unwrapStrictResponse(await flex.handleResponse(response))
    }, 'Delete availability')
  }

  async createTeamAvailability(availabilityData) {
    return this.execute(async () => {
      this.validateParams({ availabilityData }, ['availabilityData'])

      const response = await flex.flexFetch(this.endpoint + 'team/post', {
        method: 'POST',
        body: JSON.stringify(availabilityData),
        credentials: 'include',
      })

      return this._unwrapStrictResponse(await flex.handleResponse(response))
    }, 'Create team availability')
  }

  async updateTeamAvailability(availabilityData) {
    return this.execute(async () => {
      this.validateParams({ availabilityData }, ['availabilityData'])

      if (!availabilityData.shared_team_slot_id) {
        throw new Error('Shared team slot ID is required for updates')
      }

      const response = await flex.flexFetch(this.endpoint + 'team/update', {
        method: 'PUT',
        body: JSON.stringify(availabilityData),
        credentials: 'include',
      })

      return this._unwrapStrictResponse(await flex.handleResponse(response))
    }, 'Update team availability')
  }

  async deleteTeamAvailability(sharedTeamSlotId) {
    return this.execute(async () => {
      this.validateParams({ sharedTeamSlotId }, ['sharedTeamSlotId'])

      const response = await flex.flexFetch(this.endpoint + 'team/delete', {
        method: 'DELETE',
        body: JSON.stringify({ shared_team_slot_id: sharedTeamSlotId }),
        credentials: 'include',
      })

      return this._unwrapStrictResponse(await flex.handleResponse(response))
    }, 'Delete team availability')
  }

  async addTeamException(sharedTeamSlotId, exceptionDate) {
    return this.execute(async () => {
      this.validateParams({ sharedTeamSlotId, exceptionDate }, [
        'sharedTeamSlotId',
        'exceptionDate',
      ])

      const response = await flex.flexFetch(
        this.endpoint + 'team/addException',
        {
          method: 'POST',
          body: JSON.stringify({
            shared_team_slot_id: sharedTeamSlotId,
            exceptionDate,
          }),
          credentials: 'include',
        },
      )

      return this._unwrapStrictResponse(await flex.handleResponse(response))
    }, 'Add exception to team recurring availability')
  }

  async deleteTeamAvailabilityForDay({
    team_id,
    league_id,
    division_id,
    matchDate,
  }) {
    return this.execute(async () => {
      this.validateParams({ league_id, division_id, matchDate }, [
        'league_id',
        'division_id',
        'matchDate',
      ])

      const response = await flex.flexFetch(this.endpoint + 'team/deleteDay', {
        method: 'DELETE',
        body: JSON.stringify({ team_id, league_id, division_id, matchDate }),
        credentials: 'include',
      })

      return this._unwrapStrictResponse(await flex.handleResponse(response))
    }, 'Delete team day availability')
  }

  /**
   * Delete all availability for a specific day
   * @param {string} matchDate - The match date (ISO string)
   * @param {number} leagueId - The league ID
   * @param {number} divisionId - The division ID
   * @returns {Promise<Object>} Service response object
   */
  async deleteAvailabilityForDay(matchDate, leagueId, divisionId) {
    return this.execute(async () => {
      this.validateParams({ matchDate, leagueId, divisionId }, [
        'matchDate',
        'leagueId',
        'divisionId',
      ])

      const response = await flex.flexFetch(this.endpoint + 'deleteDay', {
        method: 'DELETE',
        body: JSON.stringify({ matchDate, leagueId, divisionId }),
        credentials: 'include',
      })

      return this._unwrapStrictResponse(await flex.handleResponse(response))
    }, 'Delete day availability')
  }

  /**
   * Add an exception date to a recurring availability pattern
   * @param {number} availabilityId - ID of the recurring availability
   * @param {string} exceptionDate - ISO date string to exclude from recurrence
   * @returns {Promise<Object>} Service response object
   */
  async addException(availabilityId, exceptionDate) {
    return this.execute(async () => {
      this.validateParams({ availabilityId, exceptionDate }, [
        'availabilityId',
        'exceptionDate',
      ])

      const response = await flex.flexFetch(this.endpoint + 'addException', {
        method: 'POST',
        body: JSON.stringify({ id: availabilityId, exceptionDate }),
        credentials: 'include',
      })

      return this._unwrapStrictResponse(await flex.handleResponse(response))
    }, 'Add exception to recurring availability')
  }

  /**
   * Get availability overlaps for a league (find common availability between players)
   * @param {number} leagueId - ID of the league
   * @returns {Promise<Object>} Service response object with overlap data
   */
  async getAvailabilityOverlaps(leagueId) {
    return this.execute(async () => {
      this.validateParams({ leagueId }, ['leagueId'])

      const response = await flex.flexFetch(
        this.endpoint + `overlaps?league_id=${leagueId}`,
        {
          credentials: 'include',
        },
      )

      return this._unwrapStrictResponse(await flex.handleResponse(response))
    }, 'Get availability overlaps')
  }

  /**
   * Bulk create multiple availability slots
   * @param {Array} availabilitySlots - Array of availability data objects
   * @returns {Promise<Object>} Service response object with bulk creation results
   */
  async createBulkAvailability(availabilitySlots) {
    return this.execute(async () => {
      this.validateParams({ availabilitySlots }, ['availabilitySlots'])

      const results = []
      const errors = []

      for (const slot of availabilitySlots) {
        try {
          const result = await this.createAvailability(slot)
          if (result.success) {
            results.push(result.data)
          } else {
            errors.push({ slot, error: result.error })
          }
        } catch (error) {
          errors.push({ slot, error: error.message })
        }
      }

      return {
        created: results.length,
        errors: errors.length,
        results,
        errorDetails: errors,
      }
    }, 'Create bulk availability')
  }

  /**
   * Update an existing availability slot
   * @param {Object} availabilityData - Availability data object including id
   * @returns {Promise<Object>} Service response object
   */
  async updateAvailability(availabilityData) {
    return this.execute(async () => {
      this.validateParams({ availabilityData }, ['availabilityData'])

      if (!availabilityData.id) {
        throw new Error('Availability ID is required for updates')
      }

      const response = await flex.flexFetch(this.endpoint + 'update', {
        method: 'PUT',
        body: JSON.stringify(availabilityData),
        credentials: 'include',
      })

      return this._unwrapStrictResponse(await flex.handleResponse(response))
    }, 'Update availability')
  }

  /**
   * Check if user has sufficient availability for finding matches
   * @returns {Promise<Object>} Service response object with availability status
   */
  async checkAvailabilityStatus() {
    return this.execute(async () => {
      const availabilityResponse = await this.getMyAvailability()

      if (!availabilityResponse.success) {
        throw new Error('Failed to check availability status')
      }

      const availability = availabilityResponse.data
      const hasAvailability = availability && availability.length > 0

      return {
        hasAvailability,
        availabilityCount: availability ? availability.length : 0,
        needsSetup: !hasAvailability,
        suggestion: hasAvailability
          ? 'You have availability set up for finding matches'
          : 'Set your availability to start finding matches',
      }
    }, 'Check availability status')
  }

  /**
   * Find matches based on availability overlaps
   * @param {number} leagueId - ID of the league
   * @param {Object} filters - Optional filters for matches
   * @returns {Promise<Object>} Service response object with potential matches
   */
  async findPotentialMatches(leagueId, filters = {}) {
    return this.execute(async () => {
      const overlapsResponse = await this.getAvailabilityOverlaps(leagueId)

      if (!overlapsResponse.success) {
        throw new Error('Failed to find potential matches')
      }

      const overlaps = overlapsResponse.data

      // Process and filter overlaps based on criteria
      const potentialMatches = overlaps.filter((overlap) => {
        // Apply any filters here
        if (filters.minDuration && overlap.duration < filters.minDuration) {
          return false
        }
        return true
      })

      return {
        matches: potentialMatches,
        total: potentialMatches.length,
        hasMatches: potentialMatches.length > 0,
      }
    }, 'Find potential matches')
  }

  /**
   * Get league-wide availability data for weekly calendar view
   * @param {number} leagueId - ID of the league
   * @param {number} divisionId - ID of the division (optional)
   * @returns {Promise<Object>} Service response object with user and opponent availability
   */
  async getLeagueAvailability(leagueId, divisionId) {
    return this.execute(async () => {
      this.validateParams({ leagueId }, ['leagueId'])

      let url = this.endpoint + `leagueAvailability?league_id=${leagueId}`
      if (divisionId) {
        url += `&division_id=${divisionId}`
      }

      const response = await flex.flexFetch(url, {
        credentials: 'include',
      })

      return this._unwrapStrictResponse(await flex.handleResponse(response))
    }, 'Get league availability')
  }

  _unwrapStrictResponse(result) {
    if (
      result &&
      typeof result === 'object' &&
      'success' in result &&
      'data' in result
    ) {
      return result.data
    }

    return result
  }
}

// Export singleton instance
const availabilityService = new AvailabilityService()
export default availabilityService
